import { display, buildEndpointHost, mapValues, getCookie } from '@datadog/browser-core';
const REMOTE_CONFIGURATION_VERSION = 'v1';
const SUPPORTED_FIELDS = [
    'applicationId',
    'service',
    'env',
    'version',
    'sessionSampleRate',
    'sessionReplaySampleRate',
    'defaultPrivacyLevel',
    'enablePrivacyForActionName',
    'traceSampleRate',
    'trackSessionAcrossSubdomains',
    'allowedTracingUrls',
    'allowedTrackingOrigins',
];
export async function fetchAndApplyRemoteConfiguration(initConfiguration, supportedContextManagers) {
    const fetchResult = await fetchRemoteConfiguration(initConfiguration);
    if (!fetchResult.ok) {
        display.error(fetchResult.error);
        return;
    }
    return applyRemoteConfiguration(initConfiguration, fetchResult.value, supportedContextManagers);
}
export function applyRemoteConfiguration(initConfiguration, rumRemoteConfiguration, supportedContextManagers) {
    // intents:
    // - explicitly set each supported field to limit risk in case an attacker can create configurations
    // - check the existence in the remote config to avoid clearing a provided init field
    const appliedConfiguration = { ...initConfiguration };
    SUPPORTED_FIELDS.forEach((option) => {
        if (option in rumRemoteConfiguration) {
            appliedConfiguration[option] = resolveConfigurationProperty(rumRemoteConfiguration[option]);
        }
    });
    Object.keys(supportedContextManagers).forEach((context) => {
        if (rumRemoteConfiguration[context] !== undefined) {
            resolveContextProperty(supportedContextManagers[context], rumRemoteConfiguration[context]);
        }
    });
    return appliedConfiguration;
}
function resolveConfigurationProperty(property) {
    if (Array.isArray(property)) {
        return property.map(resolveConfigurationProperty);
    }
    if (isObject(property)) {
        if (isSerializedOption(property)) {
            const type = property.rcSerializedType;
            switch (type) {
                case 'string':
                    return property.value;
                case 'regex':
                    return resolveRegex(property.value);
                case 'dynamic':
                    return resolveDynamicOption(property);
                default:
                    display.error(`Unsupported remote configuration: "rcSerializedType": "${type}"`);
                    return;
            }
        }
        return mapValues(property, resolveConfigurationProperty);
    }
    return property;
}
function isObject(property) {
    return typeof property === 'object' && property !== null;
}
function isSerializedOption(value) {
    return 'rcSerializedType' in value;
}
function resolveRegex(pattern) {
    try {
        return new RegExp(pattern);
    }
    catch (_a) {
        display.error(`Invalid regex in the remote configuration: '${pattern}'`);
    }
}
function resolveContextProperty(contextManager, contextItems) {
    contextItems.forEach(({ key, value }) => {
        contextManager.setContextProperty(key, resolveConfigurationProperty(value));
    });
}
function resolveDynamicOption(property) {
    const strategy = property.strategy;
    switch (strategy) {
        case 'cookie':
            return resolveCookieValue(property);
        case 'dom':
            return resolveDomValue(property);
        default:
            display.error(`Unsupported remote configuration: "strategy": "${strategy}"`);
            return;
    }
}
function resolveCookieValue({ name, extractor }) {
    const cookieValue = getCookie(name);
    if (extractor !== undefined && cookieValue !== undefined) {
        return extractValue(extractor, cookieValue);
    }
    return cookieValue;
}
function resolveDomValue({ selector, attribute, extractor, }) {
    let element;
    try {
        element = document.querySelector(selector);
    }
    catch (_a) {
        element = null;
        display.error(`Invalid selector in the remote configuration: '${selector}'`);
    }
    if (element === null || isForbidden(element, attribute)) {
        return;
    }
    const domValue = attribute !== undefined ? element.getAttribute(attribute) : element.textContent;
    if (extractor !== undefined && domValue !== null) {
        return extractValue(extractor, domValue);
    }
    return domValue !== null && domValue !== void 0 ? domValue : undefined;
}
function isForbidden(element, attribute) {
    return element.getAttribute('type') === 'password' && attribute === 'value';
}
function extractValue(extractor, candidate) {
    const resolvedExtractor = resolveRegex(extractor.value);
    if (resolvedExtractor === undefined) {
        return;
    }
    const regexResult = resolvedExtractor.exec(candidate);
    if (regexResult === null) {
        return;
    }
    const [match, capture] = regexResult;
    return capture ? capture : match;
}
export async function fetchRemoteConfiguration(configuration) {
    let response;
    try {
        response = await fetch(buildEndpoint(configuration));
    }
    catch (_a) {
        response = undefined;
    }
    if (!response || !response.ok) {
        return {
            ok: false,
            error: new Error('Error fetching the remote configuration.'),
        };
    }
    const remoteConfiguration = await response.json();
    if (remoteConfiguration.rum) {
        return {
            ok: true,
            value: remoteConfiguration.rum,
        };
    }
    return {
        ok: false,
        error: new Error('No remote configuration for RUM.'),
    };
}
export function buildEndpoint(configuration) {
    if (configuration.remoteConfigurationProxy) {
        return configuration.remoteConfigurationProxy;
    }
    return `https://sdk-configuration.${buildEndpointHost('rum', configuration)}/${REMOTE_CONFIGURATION_VERSION}/${encodeURIComponent(configuration.remoteConfigurationId)}.json`;
}
//# sourceMappingURL=remoteConfiguration.js.map